/* eslint-disable max-lines-per-function, max-lines */
import debounce from "lodash.debounce";
import { initTooltip } from "common/customer/tooltip";
import { trackEvent } from "./product-comment-tracker";
import apiComments from "./product-comments-api";
import initCommentsModal, {
  provideQuestion
} from "./product-comments-modal/product-comments-modal";
import "./product-comments-item/product-comments-item";

import "./product-comments.scss";

const STORE = {
  isInitialEmptyList: false,
  currentPage: 1,
  query: ""
};

const $getCommentsList = () => document.querySelector(".js-product-comments-list");
const $getCommentsForm = () => document.querySelector(".js-comments-search-form");

const updateMoreAnswersButton = total => {
  const $moreAnswersButton = document.querySelector(".js-load-more-questions");
  if (!$moreAnswersButton) return;

  const $count = $moreAnswersButton.querySelector(".js-load-more-questions-count");
  $moreAnswersButton.classList.toggle("hidden", total <= 0);
  $count.innerText = total;
};

const setDisablingSearchInput = isDisabled => {
  const $searchInput = document.querySelector(".js-search-comments");
  if (!$searchInput) return;

  $searchInput.disabled = isDisabled;
};

const checkEmptyList = (total, action) => {
  const isZeroComments = total <= -5 && action === "init";
  const $questionButton = document.querySelector(".js-product-comments-question-wrapper");
  const $backToListButton = document.querySelector(".js-back-to-comments-list");
  $questionButton?.classList.toggle("product-comments__new-question--empty-list", isZeroComments);
  if (isZeroComments) STORE.isInitialEmptyList = true; // Will only once initialization after loading
  $backToListButton?.classList.toggle("hidden", STORE.isInitialEmptyList);
};

const appendCommentsList = (comments, replace) => {
  const $insertPlace = $getCommentsList();
  const $newAnswer = document.createElement("div");
  $newAnswer.classList.add("product-comments__list-page");
  $newAnswer.innerHTML = comments;
  if (replace) $insertPlace.innerHTML = "";
  $insertPlace?.appendChild($newAnswer);
};

const loadComments = action => {
  setDisablingSearchInput(true);
  apiComments
    .loadComments({ query: STORE.query, page: STORE.currentPage })
    .then(({ total, comments }) => {
      appendCommentsList(comments, action === "replace");
      updateMoreAnswersButton(total);
      checkEmptyList(total, action);
      setDisablingSearchInput(false);
      initTooltip();
      STORE.currentPage += 1;
    });
};

const handleSearch = () => {
  const $clearSearchButtons = document.querySelectorAll(".js-search-clear-comments");
  const $input = document.querySelector(".js-search-comments");
  const setVisibilityClearButton = isHidden => {
    $clearSearchButtons.forEach($clearSearchButton => {
      $clearSearchButton.classList.toggle("hidden", isHidden);
    });
  };
  const sendRequest = () => {
    STORE.query = $input.value;
    STORE.currentPage = 1;
    loadComments("replace");
    trackEvent("product_detail_question_searched", { question_search_string: $input.value });
  };
  const debouncedRequest = debounce(sendRequest, 2000);

  $input?.addEventListener("keyup", e => {
    if (e.which === 13) {
      e.preventDefault();
      debouncedRequest.cancel();
      sendRequest();
    } else {
      debouncedRequest();
    }
    setVisibilityClearButton($input.value === "");
  });
  $clearSearchButtons.forEach($clearSearchButton => {
    $clearSearchButton.addEventListener("click", () => {
      $input.value = "";
      setVisibilityClearButton(true);
      sendRequest();
    });
  });
};

const handleShowQuestionModal = () => {
  const $handler = document.querySelector(".js-show-question-modal");

  $handler?.addEventListener("click", () => {
    provideQuestion(STORE.query);
    trackEvent("product_detail_submit_question_modal_open");
  });
};

const handleLoadComments = () => {
  const $handler = document.querySelector(".js-load-more-questions");

  $handler?.addEventListener("click", event => {
    event.preventDefault();
    loadComments();
  });
};

const handleLoadAnswers = ($button, event) => {
  event.preventDefault();

  $button.parentElement.querySelectorAll(".js-products-comments-answer.hidden").forEach($answer => {
    $answer.classList.remove("hidden");
  });
  $button.parentElement.removeChild(event.target);
};

const handleClickVote = ($button, event) => {
  event.preventDefault();
  const $wrapper = $button.parentElement;
  const $ratingContainer = $wrapper.querySelector(".js-rating");
  const { id, commentId, answerId } = $ratingContainer.dataset;
  const { kind, defaultRating } = $button.dataset;
  const voteType = kind === "0" ? "up" : "down";

  apiComments.voteComment({ id: Number(id), kind: Number(kind) }).then(rating => {
    $ratingContainer.innerText = rating;
    $wrapper.querySelectorAll(".js-comment-vote").forEach($vote => {
      $vote.classList.toggle(
        "active",
        $button === $vote && String(defaultRating) !== String(rating)
      );

      if ($button === $vote) {
        trackEvent("product_detail_answer_voted", {
          vote_type: voteType,
          question_id: commentId,
          answer_id: answerId
        });
      }
    });
  });
};

const setAnswerError = ($wrapper, error) => {
  const $errorMessage = $wrapper.querySelector(".product-comments-item__error-message");
  $errorMessage.innerText = error;
};

const handleCancelAnswer = ($button, event) => {
  event.preventDefault();
  const $wrapper = $button.closest(".js-comments-answers-container");
  $wrapper.querySelector(".js-answer-wrapper[open]")?.removeAttribute("open");
  setAnswerError($wrapper, "");
};

const handleSubmitAnswer = ($button, event) => {
  event.preventDefault();
  const $wrapper = event.target.closest(".js-comments-answers-container");
  const $textarea = $wrapper.querySelector(".js-answer-input");
  const { id } = $textarea.dataset;
  const answer = $textarea.value.trim();

  apiComments
    .addComment({ answer, id })
    .then(comment => {
      const $answerBox = $wrapper.querySelector(".js-answer-wrapper");
      const $insertPlace = $wrapper.querySelector(".js-product-comments-answers-list");
      const $newAnswer = document.createElement("div");
      $newAnswer.innerHTML = comment;
      $insertPlace.appendChild($newAnswer.firstChild);
      $textarea.value = "";
      $answerBox.classList.add("hidden");
      initTooltip();
      trackEvent("product_detail_answer_to_question_submitted", { question_id: id, answer });
    })
    .catch(error => {
      setAnswerError($wrapper, error);
    });
};

const handleAddAnswer = ($button, event) => {
  const $wrapper = event.target.closest(".js-comments-answers-container");
  const $textarea = $wrapper.querySelector(".js-answer-input");
  const { id } = $textarea.dataset;
  trackEvent("product_detail_answer_to_question_clicked", { question_id: id });
};

const checkAvailableAnswer = (anwser, $submitButton) => {
  // eslint-disable-next-line no-param-reassign
  $submitButton.disabled = anwser.length === 0;
};

const handleTextareaKeyUp = $textarea => {
  const trimmedValue = $textarea.value.trim();
  const $wrapper = $textarea.closest(".js-comments-answers-container");
  const $submitButton = $wrapper.querySelector(".js-submit-answer");
  const isShowError = trimmedValue.length === 0;
  checkAvailableAnswer(trimmedValue, $submitButton);
  $textarea.classList.toggle("error", isShowError);
  setAnswerError($wrapper, isShowError ? "Answer field cannot be empty" : "");
};

const initComments = () => {
  loadComments("init");
  initCommentsModal();
  handleSearch();
  handleShowQuestionModal();
  handleLoadComments();

  const $commentsList = $getCommentsList();
  const $commentsForm = $getCommentsForm();

  $commentsList?.addEventListener("click", event => {
    const $handler = event.target;
    if ($handler.className.includes("js-more-answers-button")) handleLoadAnswers($handler, event);
    if ($handler.className.includes("js-comment-vote")) handleClickVote($handler, event);
    if ($handler.className.includes("js-submit-answer")) handleSubmitAnswer($handler, event);
    if ($handler.className.includes("js-cancel-answer")) handleCancelAnswer($handler, event);
    if ($handler.className.includes("js-add-answer-btn")) handleAddAnswer($handler, event);
  });

  $commentsList?.addEventListener("keyup", event => {
    const $handler = event.target;
    if ($handler.className.includes("js-answer-input")) handleTextareaKeyUp($handler);
  });

  $commentsForm?.addEventListener("submit", event => {
    event.preventDefault();
    return false;
  });
};

document.addEventListener("DOMContentLoaded", () => {
  const $wrapper = document.querySelector(".js-products-comments-wrapper");
  if (!$wrapper) return;

  if ($wrapper.dataset.dynamicDataLoaded === "true") {
    initComments();
  } else {
    $wrapper.addEventListener(
      "dynamicCommentsLoaded",
      () => {
        initComments();
      },
      false
    );
  }
});
